import { useState, useEffect } from 'react';
import AdminTenantCountries from './AdminTenantCountries';
import AdminTenantFeatures from './AdminTenantFeatures';
import ApiService from '../services/ApiService';
import '../styles/components.css';

const selectStyle = {
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  backgroundColor: 'var(--bg-card)',
  color: 'var(--text-primary)',
  fontSize: 13,
};

const iconBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: 6,
  border: '1px solid transparent',
  background: 'transparent',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  transition: 'all 0.15s',
  fontSize: 14,
};

const AdminTenantManagement = () => {
  const [tenants, setTenants] = useState([]);
  const [filteredTenants, setFilteredTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [templateFilter, setTemplateFilter] = useState('');
  const [countriesTenant, setCountriesTenant] = useState(null);
  const [featuresTenant, setFeaturesTenant] = useState(null);

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const [tenantsResponse, socialStatusResponse] = await Promise.all([
        ApiService.getAllTenants(),
        ApiService.getTenantsSocialStatus()
      ]);

      const tenantsWithSocials = tenantsResponse.data.map(tenant => {
        const parentTenant = tenant.parent ?
          tenantsResponse.data.find(t => t.id === tenant.parent) : null;

        return {
          ...tenant,
          socialStatus: socialStatusResponse.data.find(s => s.tenant_id === tenant.id) || {},
          parentName: parentTenant ? parentTenant.name : null
        };
      });

      setTenants(tenantsWithSocials);
      setFilteredTenants(tenantsWithSocials);
    } catch (err) {
      setError('Failed to load tenants: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = tenants;

    if (searchTerm) {
      filtered = filtered.filter(tenant =>
        tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (tenant.model_name && tenant.model_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (typeFilter) {
      filtered = filtered.filter(tenant => tenant.type === typeFilter);
    }

    if (templateFilter) {
      filtered = filtered.filter(tenant => tenant.template === templateFilter);
    }

    setFilteredTenants(filtered);
  }, [tenants, searchTerm, typeFilter, templateFilter]);

  const formatTenantType = (type) => {
    if (!type) return 'Unknown';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const getTenantBadgeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'sponsor': return 'fc-badge-blue';
      case 'league': return 'fc-badge-green';
      case 'club': return 'fc-badge-blue';
      case 'athlete': return 'fc-badge-gold';
      case 'association':
      case 'organization': return 'fc-badge-gray';
      default: return 'fc-badge-gray';
    }
  };

  const renderSocialStatus = (socialStatus) => {
    const facebookStatus = socialStatus.facebook_status;
    const instagramStatus = socialStatus.instagram_status;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span title={`Facebook: ${facebookStatus || 'Not connected'}`}>
          <i className="fab fa-facebook" style={{ fontSize: 16, color: facebookStatus === 'active' ? 'var(--positive)' : 'var(--negative)' }} />
        </span>
        <span title={`Instagram: ${instagramStatus || 'Not connected'}`}>
          <i className="fab fa-instagram" style={{ fontSize: 16, color: instagramStatus === 'active' ? 'var(--positive)' : 'var(--negative)' }} />
        </span>
      </div>
    );
  };

  const uniqueTypes = [...new Set(tenants.map(t => t.type).filter(Boolean))].sort();
  const uniqueTemplates = [...new Set(tenants.map(t => t.template).filter(Boolean))].sort();

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="fc-card">
        <div className="fc-card-title" style={{ marginBottom: 16 }}>Tenant Management</div>

        {error && (
          <div className="fc-callout fc-callout-error" style={{ marginBottom: 16 }}>{error}</div>
        )}

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 0' }}>
            <div className="fc-spinner" />
            <div style={{ marginTop: 8, color: 'var(--text-secondary)', fontSize: 13 }}>Loading tenants...</div>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 12, marginBottom: 16, alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search tenants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={selectStyle}
              />
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={selectStyle}>
                <option value="">All Types</option>
                {uniqueTypes.map(type => (
                  <option key={type} value={type}>{formatTenantType(type)}</option>
                ))}
              </select>
              <select value={templateFilter} onChange={(e) => setTemplateFilter(e.target.value)} style={selectStyle}>
                <option value="">All Templates</option>
                {uniqueTemplates.map(template => (
                  <option key={template} value={template}>{template}</option>
                ))}
              </select>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                {filteredTenants.length} of {tenants.length} tenants
              </span>
            </div>

            {filteredTenants.length === 0 ? (
              <div className="fc-callout fc-callout-info">
                {tenants.length === 0 ? 'No tenants found.' : 'No tenants match your search criteria.'}
              </div>
            ) : (
              <table className="fc-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Model</th>
                    <th>Type</th>
                    <th>Template</th>
                    <th>Parent</th>
                    <th>Socials</th>
                    <th>Onboarded</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTenants.map((tenant) => (
                    <tr key={tenant.id}>
                      <td><code>{tenant.id}</code></td>
                      <td>
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{tenant.name}</span>
                      </td>
                      <td>
                        <span
                          className={`fc-badge ${tenant.model_name ? 'fc-badge-blue' : 'fc-badge-gray'}`}
                          title={tenant.model_name ? `Model: ${tenant.model_name}` : 'Default model: Visua'}
                        >
                          {tenant.model_name || 'Visua'}
                        </span>
                      </td>
                      <td>
                        <span className={`fc-badge ${getTenantBadgeColor(tenant.type)}`}>
                          {formatTenantType(tenant.type)}
                        </span>
                      </td>
                      <td>
                        {tenant.template ? (
                          <span className="fc-badge fc-badge-outline">{tenant.template}</span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>-</span>
                        )}
                      </td>
                      <td>
                        {tenant.parentName ? (
                          <span className="fc-badge fc-badge-outline">{tenant.parentName}</span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>-</span>
                        )}
                      </td>
                      <td>{renderSocialStatus(tenant.socialStatus || {})}</td>
                      <td>
                        {tenant.onboarding_completed ? (
                          <i className="fas fa-check" style={{ fontSize: 16, color: 'var(--positive)' }} />
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>-</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                          <button
                            style={iconBtnStyle}
                            title={`Countries for ${tenant.name}`}
                            onClick={() => setCountriesTenant(tenant)}
                          >
                            <i className="fas fa-globe" />
                          </button>
                          <button
                            style={{
                              ...iconBtnStyle,
                              color: tenant.features && Object.values(tenant.features).some(Boolean) ? 'var(--positive)' : 'var(--text-muted)',
                            }}
                            title={`Features for ${tenant.name}`}
                            onClick={() => setFeaturesTenant(tenant)}
                          >
                            <i className="fas fa-toggle-on" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      {/* Countries Management Modal */}
      <div className={`modal-overlay${countriesTenant ? ' open' : ''}`} onClick={() => setCountriesTenant(null)}>
        <div className="modal-panel" onClick={e => e.stopPropagation()}>
          <div className="modal-panel-header">
            <div className="modal-panel-title">Countries — {countriesTenant?.name}</div>
            <button className="modal-panel-close" onClick={() => setCountriesTenant(null)}>&times;</button>
          </div>
          <div className="modal-panel-body">
            {countriesTenant && (
              <AdminTenantCountries
                tenantId={countriesTenant.id}
                tenantName={countriesTenant.name}
                onClose={() => setCountriesTenant(null)}
              />
            )}
          </div>
        </div>
      </div>

      {/* Features Management Modal */}
      <div className={`modal-overlay${featuresTenant ? ' open' : ''}`} onClick={() => setFeaturesTenant(null)}>
        <div className="modal-panel" onClick={e => e.stopPropagation()}>
          <div className="modal-panel-header">
            <div className="modal-panel-title">Features — {featuresTenant?.name}</div>
            <button className="modal-panel-close" onClick={() => setFeaturesTenant(null)}>&times;</button>
          </div>
          <div className="modal-panel-body">
            {featuresTenant && (
              <AdminTenantFeatures
                tenantId={featuresTenant.id}
                tenantName={featuresTenant.name}
                onClose={() => { setFeaturesTenant(null); fetchTenants(); }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


export default AdminTenantManagement;
